import React from 'react';

import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import {
  Checkbox,
  FormHelperText,
  ListItemText,
  SelectProps,
} from '@mui/material';

import { Control, Controller, get, RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface SelectOption {
  key: string;
  label: string;
  shorthand?: string;
}

type CheckboxMultiSelectInputProps = Omit<
  SelectProps<string[]>,
  'value' | 'onChange' | 'children'
> & {
  errors?: Record<string, any>;
  control: Control<any>;
  rules?: Exclude<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >;
  helperText?: string;
  options: SelectOption[];
  defaultValue?: string[];
  name: string;
};
const renderValue = (selected: unknown, options: SelectOption[]): string => {
  return (selected as string[])
    .map((element) => {
      const option = options.find((option) => option.key === element);
      return option?.shorthand || option?.label || '';
    })
    .filter(Boolean)
    .join(', ');
};

export const CheckboxMultiSelectInput: React.FC<CheckboxMultiSelectInputProps> =
  // eslint-disable-next-line max-lines-per-function
  ({
    errors,
    rules,
    name,
    control,
    helperText,
    options,
    defaultValue,
    label,
    ...selectProps
  }) => {
    const { t } = useTranslation();
    const error = get(errors, name);
    const { variant: _variant, ...restSelectProps } = selectProps;
    return (
      <Controller
        control={control}
        name={name}
        rules={rules}
        defaultValue={defaultValue}
        render={({ field: { value, onChange } }) => {
          const selectedValues = Array.isArray(value) ? value : [];
          return (
            <FormControl fullWidth variant="outlined">
              <InputLabel>{label}</InputLabel>
              <Select
                multiple
                value={selectedValues}
                label={label as any}
                renderValue={(selected) => renderValue(selected, options)}
                onChange={(e) => onChange(e.target.value)}
                {...restSelectProps}
              >
                {options.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    <Checkbox
                      color="default"
                      checked={
                        selectedValues.findIndex(
                          (e: string) => e === option.key,
                        ) > -1
                      }
                    />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
              {(error?.message || helperText) && (
                <FormHelperText error={Boolean(error)}>
                  {error?.message || helperText}
                </FormHelperText>
              )}
            </FormControl>
          );
        }}
      />
    );
  };
